/**
 * Dialog para transferir conversa para outro agente
 *
 * Permite selecionar um agente da equipe e adicionar
 * uma nota opcional de transferência.
 *
 * v2.0.0 - 2026-06-03 - Redesign visual "Dark Executive":
 *   - Header refinado com X custom e borda inferior sutil
 *   - Barra de busca para filtrar agentes em tempo real
 *   - Lista compacta com avatar, badges (Você/Admin) e indicador de seleção
 *   - Nota em campo coerente com o resto do sistema
 *   - Footer com fundo `bg-muted/30` e botões padronizados
 *   - 100% baseado em tokens semânticos do design system (light/dark)
 *   - Lógica de transferência preservada integralmente
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Search, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConversationTransfer, TransferableAgent } from '@/hooks/useConversationTransfer';
import { useAuth } from '@/hooks/useAuth';

interface TransferConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  currentAgentId?: string | null;
  onSuccess?: () => void;
  // Dados para mensagem de sistema no webchat
  inboxId?: string;
  channelType?: string;
}

export function TransferConversationDialog({
  open,
  onOpenChange,
  conversationId,
  currentAgentId,
  onSuccess,
  inboxId,
  channelType,
}: TransferConversationDialogProps) {
  const { loading, agents, fetchAvailableAgents, transferToAgent } = useConversationTransfer();
  const { user } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState<TransferableAgent | null>(null);
  const [note, setNote] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Carrega agentes quando o dialog abre
  useEffect(() => {
    if (open) {
      fetchAvailableAgents();
      setSelectedAgent(null);
      setNote('');
      setSearchTerm('');
    }
  }, [open, fetchAvailableAgents]);

  // Filtra agentes por nome ou email (case-insensitive)
  const filteredAgents = agents.filter((agent) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      agent.full_name?.toLowerCase().includes(search) ||
      agent.email.toLowerCase().includes(search)
    );
  });

  // Helper para iniciais do avatar
  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.charAt(0).toUpperCase();
  };

  // Handler para transferir conversa
  const handleTransfer = async () => {
    if (!selectedAgent) return;

    setTransferring(true);

    // Dados para broadcast se for webchat
    const webchatData = channelType === 'webchat' && inboxId ? { inboxId } : undefined;

    const success = await transferToAgent(
      conversationId,
      selectedAgent.user_id,
      selectedAgent.full_name || 'Agente',
      note,
      webchatData
    );
    setTransferring(false);

    if (success) {
      onOpenChange(false);
      onSuccess?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px] p-0 gap-0 overflow-hidden"
        hideCloseButton
      >
        {/* ============== HEADER ============== */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between border-b border-border/60">
          <div className="min-w-0 flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Users className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground tracking-tight">
                Transferir Conversa
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Selecione um agente para transferir esta conversa.
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => onOpenChange(false)}
            title="Fechar"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* ============== SEARCH ============== */}
        {!loading && agents.length > 0 && (
          <div className="px-5 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar agente..."
                className="pl-9 h-9 bg-muted/40 border-border/60 focus-visible:ring-ring/30"
              />
            </div>
          </div>
        )}

        {/* ============== CONTEÚDO ============== */}
        <div className="px-5 py-3 overflow-y-auto max-h-[368px] space-y-3">
          {/* ===== Lista de agentes ===== */}
          <div>
            <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
              Agentes {agents.length > 0 && `· ${agents.length}`}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm">
                  Nenhum outro agente disponível.
                </p>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="text-center py-5 text-sm text-muted-foreground">
                Nenhum agente corresponde a "{searchTerm}".
              </div>
            ) : (
              <div className="flex flex-col gap-1 max-h-[208px] overflow-y-auto pr-1">
                {filteredAgents.map((agent) => {
                  const isSelected = selectedAgent?.id === agent.id;
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      onClick={() => setSelectedAgent(agent)}
                      className={cn(
                        'w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all border',
                        isSelected
                          ? 'bg-muted/60 border-border ring-1 ring-ring/30'
                          : 'border-transparent hover:bg-muted/40 hover:border-border/60'
                      )}
                    >
                      <SmartAvatar
                        src={agent.avatar_url}
                        name={agent.full_name}
                        email={agent.email}
                        size="sm"
                        className="h-7 w-7 shrink-0"
                        fallbackClassName="text-[10px]"
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground truncate">
                            {agent.full_name || 'Sem nome'}
                          </p>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {/* Badge "Você" para o usuário logado */}
                            {agent.user_id === user?.id && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 h-5 border-primary text-primary"
                              >
                                Você
                              </Badge>
                            )}
                            {/* Badge "Admin" em verde para harmonizar com o padrão */}
                            {agent.is_owner && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] px-1.5 h-5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              >
                                Admin
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {agent.email}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ===== Nota de transferência ===== */}
          {agents.length > 0 && (
            <div className="space-y-2">
              <Label
                htmlFor="transfer-note"
                className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest"
              >
                Nota de transferência (opcional)
              </Label>
              <Textarea
                id="transfer-note"
                placeholder="Adicione contexto para o próximo agente..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="resize-none bg-muted/40 border-border/60 focus-visible:ring-ring/30"
              />
              <p className="text-xs text-muted-foreground">
                Esta nota será visível apenas para a equipe.
              </p>
            </div>
          )}
        </div>

        {/* ============== FOOTER ============== */}
        <div className="px-5 py-3 bg-muted/30 border-t border-border/60 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={transferring}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedAgent || transferring}
            className="shadow-sm"
          >
            {transferring ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Transferindo...
              </>
            ) : (
              'Transferir'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
